import React, { useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  BlockStack,
  InlineStack,
  Badge,
  Banner,
  Box,
} from '@shopify/polaris';
import useConnectionStatus from '../hooks/useConnectionStatus';
import BillingCard from '../components/BillingCard';
import { authenticatedFetch } from '../utils/fetch';

/**
 * Map the machine error codes returned on the connect callback
 * (?apparelhub_error=...) and from /api/apparelhub/begin to friendly,
 * merchant-facing copy. Raw codes like `invalid_state` or `exchange_failed`
 * should never be shown verbatim. Unknown codes fall back to a generic
 * retry message (the raw code is still logged to the console for support).
 */
const CONNECT_ERROR_MESSAGES = {
  shop_owned_by_other_account:
    'This Shopify store is already connected to another ApparelHub account. Disconnect it from the other account first, or contact support if you believe this is a mistake.',
  invalid_state:
    'Your connection link expired before you finished. Click Connect ApparelHub to start again.',
  missing_code:
    "We didn't receive an approval from ApparelHub. Click Connect ApparelHub to try again.",
  exchange_failed:
    "We couldn't finish connecting to ApparelHub. Please try again in a moment, or contact support if it keeps happening.",
  begin_failed:
    "We couldn't start the connection. Please try again, or contact support if it keeps happening.",
  missing_shop:
    "We couldn't determine your store. Please reload the app and try again.",
};

const GENERIC_CONNECT_ERROR =
  "We couldn't connect your ApparelHub account. Please try again, or contact support if the problem continues.";

function friendlyConnectError(code) {
  if (!code) return GENERIC_CONNECT_ERROR;
  return CONNECT_ERROR_MESSAGES[code] || GENERIC_CONNECT_ERROR;
}

/**
 * Redirect the top-level browser out of the Shopify admin iframe.
 *
 * App Bridge v4 exposes `shopify` globally in an embedded context. Where
 * possible we use its declarative API; otherwise we fall back to setting
 * `window.top.location.href` with a `_top` target.
 */
function redirectTopLevel(url) {
  try {
    if (window.shopify && typeof window.shopify.navigate === 'function') {
      // App Bridge v4: for an absolute URL outside admin, `navigate` will
      // do a full-page redirect.
      window.shopify.navigate(url);
      return;
    }
  } catch (_) {
    // fall through
  }
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url;
      return;
    }
  } catch (_) {
    // cross-origin access denied -- use window.open targeted at _top
  }
  window.open(url, '_top');
}

export default function Settings() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [banner, setBanner] = useState(null); // {tone, message}
  const { isConnected, accountEmail, storeName, billing, loading, refresh } =
    useConnectionStatus();

  // Handle ?apparelhub_connected=1 or ?apparelhub_error=... on page load
  // (set by the /api/apparelhub/callback redirect after OAuth).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('apparelhub_connected');
    const error = params.get('apparelhub_error');

    if (connected) {
      setBanner({ tone: 'success', message: 'ApparelHub account connected.' });
      // Clean the URL so refresh doesn't re-show the banner
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh the connection status
      refresh && refresh();
    } else if (error) {
      const decodedError = decodeURIComponent(error);
      // user_denied is a deliberate cancel, not a failure — show a calm
      // info banner instead of a red critical one.
      if (decodedError === 'user_denied') {
        setBanner({
          tone: 'info',
          message:
            'Connection canceled. Click Connect ApparelHub whenever you are ready to link your account.',
        });
      } else {
        // Log the raw code for support; show friendly copy to the merchant.
        // Sales channel uniqueness, expired links, exchange failures, etc.
        // all map to clear, action-oriented messages.
        // /src/apparelhub-artifacts/epic-sales-channel-uniqueness.md
        console.warn('[APPARELHUB-CONNECT] connect error:', decodedError);
        setBanner({
          tone: 'critical',
          message: friendlyConnectError(decodedError),
        });
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Run once on mount
    // eslint-disable-next-line
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    setBanner(null);
    try {
      const response = await authenticatedFetch('/api/apparelhub/begin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok || !data.redirect_url) {
        throw new Error(data.error || data.message || 'begin_failed');
      }
      // Full top-level redirect out of the admin iframe.
      redirectTopLevel(data.redirect_url);
    } catch (error) {
      const code = (error && error.message) || 'begin_failed';
      console.error('Failed to start ApparelHub linking:', code);
      setBanner({
        tone: 'critical',
        message: friendlyConnectError(code),
      });
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      // authenticatedFetch does NOT throw on non-2xx responses, so we
      // have to inspect response.ok ourselves. Previously this code
      // blindly awaited the call, hit refresh(), and showed a green
      // "Disconnected" banner regardless of what the backend actually
      // did — including the 501-stub case where the backend silently
      // refused to disconnect anything (issue #2 on 2026-06-03).
      // Now we check ok, parse a structured error if present, and
      // surface the failure honestly.
      const response = await authenticatedFetch('/api/disconnect', { method: 'POST' });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const detail = errBody.message || errBody.error || `Server returned ${response.status}`;
        throw new Error(detail);
      }
      // Refresh BEFORE showing the success banner so we don't claim
      // success in the same paint as a still-Connected badge.
      await refresh();
      setBanner({ tone: 'info', message: 'Disconnected from ApparelHub.' });
    } catch (error) {
      console.error('Failed to disconnect:', error);
      setBanner({
        tone: 'critical',
        message: `Failed to disconnect: ${error.message || 'unknown error'}`,
      });
    }
  };

  return (
    <Page title="Settings" backAction={{ url: '/' }}>
      <Layout>
        <Layout.AnnotatedSection
          title="ApparelHub Account"
          description="Connect your ApparelHub.AI account to enable product syncing and fulfillment management."
        >
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  Connection
                </Text>
                <Badge tone={isConnected ? 'success' : 'attention'}>
                  {loading
                    ? 'Loading...'
                    : isConnected
                    ? 'Connected'
                    : 'Not connected'}
                </Badge>
              </InlineStack>

              {banner && (
                <Banner tone={banner.tone}>
                  <p>{banner.message}</p>
                </Banner>
              )}

              {isConnected ? (
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    Connected as <strong>{accountEmail}</strong>
                    {storeName ? (
                      <>
                        {' '}to ApparelHub store <strong>{storeName}</strong>
                      </>
                    ) : null}
                    .
                  </Text>
                  <Box>
                    <Button tone="critical" onClick={handleDisconnect}>
                      Disconnect
                    </Button>
                  </Box>
                </BlockStack>
              ) : (
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    Connect your ApparelHub account to sync products and fulfill
                    orders. You will be redirected to ApparelHub to approve the
                    connection.
                  </Text>
                  <Box>
                    <Button
                      variant="primary"
                      onClick={handleConnect}
                      loading={isConnecting}
                    >
                      Connect ApparelHub
                    </Button>
                  </Box>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title="Plan and billing"
          description="Your ApparelHub plan. Billing is handled by Shopify and appears on your Shopify invoice."
        >
          <BillingCard isConnected={isConnected} billing={billing} loading={loading} />
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title="About"
          description="ApparelHub.AI is an AI-powered print-on-demand merchandise platform."
        >
          <Card>
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">
                Design custom apparel with AI, manage your product catalog, and
                sell across multiple channels.
              </Text>
              <Box>
                <Button url="https://apparelhub.ai" external>
                  Visit ApparelHub.AI
                </Button>
              </Box>
            </BlockStack>
          </Card>
        </Layout.AnnotatedSection>
      </Layout>
    </Page>
  );
}
